import { Component, computed, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule } from '@ngx-translate/core';

export interface AttachmentSelectDialogData {
  title: string;
  confirmLabel: string;
  totalSize?: string;
  items: { id: number; name: string; selected: boolean }[];
}

export interface AttachmentSelectDialogResult {
  selectedIds: number[];
}

@Component({
  selector: 'attachment-select-dialog',
  imports: [MatDialogModule, MatCheckboxModule, MatButtonModule, MatIconModule, FlexLayoutModule, TranslateModule],
  template: `
    <h2 mat-dialog-title>{{ data.title | translate }}</h2>
    <mat-dialog-content>
      <div fxLayout fxLayoutAlign="space-between center" class="p-t-md m-b-sm">
        @if (data.totalSize) {
          <span> {{ 'cipoFields.totalSize' | translate }}: {{ data.totalSize }} </span>
        }
        <div fxLayout fxLayoutGap="4px">
          <button mat-icon-button (click)="selectAll()" [title]="'actions.selectAll' | translate" [attr.aria-label]="'actions.selectAll' | translate">
            <mat-icon aria-hidden="true">select_all</mat-icon>
          </button>
          <button mat-icon-button (click)="deselectAll()" [title]="'actions.deselectAll' | translate" [attr.aria-label]="'actions.deselectAll' | translate">
            <mat-icon aria-hidden="true">deselect</mat-icon>
          </button>
        </div>
      </div>
      <div class="cipo--overflow-y--auto cipo--max-height--300" fxLayout="column">
        @for (item of items(); track item.id) {
          <mat-checkbox [checked]="item.selected" (change)="toggleItem(item.id)">
            {{ item.name }}
          </mat-checkbox>
        }
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>{{ 'general.cancel' | translate }}</button>
      <button mat-flat-button color="primary" (click)="confirm()" [disabled]="!hasSelection()">
        {{ data.confirmLabel | translate }}
      </button>
    </mat-dialog-actions>
  `,
})
export class AttachmentSelectDialogComponent {
  data = inject<AttachmentSelectDialogData>(MAT_DIALOG_DATA);
  private dialogRef = inject(MatDialogRef<AttachmentSelectDialogComponent>);

  items = signal(this.data.items.map(i => ({ ...i })));
  hasSelection = computed(() => this.items().some(i => i.selected));

  selectAll(): void {
    this.items.update(items => items.map(i => ({ ...i, selected: true })));
  }

  deselectAll(): void {
    this.items.update(items => items.map(i => ({ ...i, selected: false })));
  }

  toggleItem(id: number): void {
    this.items.update(items => items.map(i => (i.id === id ? { ...i, selected: !i.selected } : i)));
  }

  confirm(): void {
    const selectedIds = this.items()
      .filter(i => i.selected)
      .map(i => i.id);
    this.dialogRef.close({ selectedIds } as AttachmentSelectDialogResult);
  }
}
